<template>
  <section class="hexagon-content">
    <transition
      @before-enter="
        (el) => beforeEnter(el, data.mainScreen ? 'left' : 'right')
      "
      @enter="enter"
      @before-leave="beforeLeave"
      @leave="
        (el, done) => leave(el, done, data.mainScreen ? 'left' : 'right')
      ">
      <div
        v-if="data.mainScreen"
        class="overview">
        <div class="card">
          <img
            v-if="data.mini_icon"
            :src="data.mini_icon.url"
            alt=""
            class="mini-icon" />
          <h2 class="hex-title">{{ data.title }}</h2>
          <div
            class="hex-description"
            v-html="data.description"></div>
        </div>
        <div class="menu">
          <div
            v-for="(menuItem, index) of data.subItems"
            :key="index"
            class="item"
            @click="setCard(index)">
            <div class="left">
              <div class="icon">
                <img
                  src="@/assets/img/arrow-button.svg"
                  alt="" />
              </div>
              <div class="title">
                <h2>{{ menuItem.title }}</h2>
              </div>
            </div>
            <div class="number">
              {{ (index + 1).toString().padStart(2, '0') }} /
              {{ data.subItems.length.toString().padStart(2, '0') }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="topics">
        <transition-group
          @before-enter="(el) => beforeEnter(el, direction)"
          @enter="enter"
          @before-leave="beforeLeave"
          @leave="(el, done) => leave(el, done, direction)">
          <template
            v-for="(menuItem, index) of data.subItems"
            :key="index">
            <template v-if="active === index">
              <div class="card">
                <div class="top-area">
                  <p class="main-title">{{ data.title }}</p>
                  <img
                    v-if="data.mini_icon"
                    :src="data.mini_icon.url"
                    alt=""
                    class="mini-icon" />
                </div>
                <div
                  v-if="!menuItem.image_video"
                  class="image"
                  :style="{
                    backgroundImage: `url(${menuItem.image_or_video.image.url})`,
                  }"></div>
                <div
                  v-else
                  class="video">
                  <video
                    autoplay
                    loop
                    muted=""
                    playsinline>
                    <source
                      :src="menuItem.image_or_video.video.url"
                      type="video/mp4" />
                  </video>
                </div>
                <div class="content">
                  <p class="hex-title">{{ menuItem.title }}</p>
                  <div
                    class="hex-description"
                    v-html="menuItem.description"></div>
                  <div class="position">
                    <div class="numbers">
                      {{ (index + 1).toString().padStart(2, '0') }} /
                      {{ data.subItems.length.toString().padStart(2, '0') }}
                    </div>
                    <div class="arrows">
                      <img
                        src="@/assets/img/arrow-button.svg"
                        alt=""
                        :style="
                          index === 0
                            ? 'filter: grayscale(1); opacity: 0.5; pointer-events: none;'
                            : ''
                        "
                        @click="previousItem(data.subItems)" />
                      <img
                        src="@/assets/img/arrow-button.svg"
                        alt=""
                        :style="
                          index + 1 === data.subItems.length
                            ? 'filter: grayscale(1); opacity: 0.5; pointer-events: none;'
                            : ''
                        "
                        @click="nextItem(data.subItems)" />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </template>
        </transition-group>
      </div>
    </transition>
  </section>
</template>
<script setup>
import { ref, onMounted, watch } from 'vue'
import gsap from 'gsap'

const emit = defineEmits(['setmain'])
defineProps(['data'])

const active = ref(0)
const direction = ref('right')

watch(
  () => active.value,
  (val, oldVal) => {
    if (val > oldVal) {
      direction.value = 'right'
    } else {
      direction.value = 'left'
    }
  }
)

const animations = () => {
  gsap.fromTo(
    '.item',
    {
      opacity: 0,
      x: 50,
    },
    {
      opacity: 1,
      x: 0,
      stagger: 0.1,
      ease: 'power2.inOut',
    }
  )
}

onMounted(() => {
  animations()
})

const nextItem = (items) => {
  if (active.value < items.length - 1) {
    active.value++
  }
}

const previousItem = () => {
  if (active.value > 0) {
    active.value--
  }
}

const setCard = (index) => {
  emit('setmain', false)
  active.value = index
}

// Animation hooks

const beforeEnter = (el, direction) => {
  if (direction === 'left') {
    gsap.set(el, {
      opacity: 0,
      x: -100,
    })
  } else {
    gsap.set(el, {
      opacity: 0,
      x: 100,
    })
  }
}

const enter = (el) => {
  gsap.to(el, {
    opacity: 1,
    x: 0,
    duration: 0.2,
    ease: 'power2.inOut',
    delay: 0.2,
  })
}

const beforeLeave = () => {}

const leave = (el, done, direction) => {
  if (direction === 'left') {
    gsap.to(el, {
      opacity: 0,
      x: 100,
      duration: 0.2,
      ease: 'power2.inOut',
      onComplete: () => {
        done()
      },
    })
  } else {
    gsap.to(el, {
      opacity: 0,
      x: -100,
      duration: 0.2,
      ease: 'power2.inOut',
      onComplete: () => {
        done()
      },
    })
  }
}
</script>
<style lang="scss" scoped>
section {
  .overview {
    @apply flex flex-col gap-2;

    .card {
      @apply bg-white p-10 rounded-lg relative;
      .mini-icon {
        @apply absolute -left-16 -top-[45px] w-[80px] h-[90px];
      }
      h2 {
        @apply mb-10;
        @apply text-[22px] font-medium leading-[26px] tracking-normal text-left text-[#353841];
        font-family: 'tt-commons-regular';
      }
      .hex-description:deep() {
        @apply mb-4;
        p {
          @apply text-[18px] font-normal leading-[22px] tracking-normal text-left text-[#353841];
        }
      }
    }
    .menu {
      @apply flex flex-col gap-2;
      .item {
        @apply flex items-center justify-between px-6 py-4 bg-white text-black rounded-lg cursor-pointer;
        .left {
          @apply flex items-center gap-6 w-[81%];
          .icon {
            @apply w-[15%];
          }
          .title {
            @apply w-[85%];
          }
          h2 {
            font-family: 'tt-commons-med';
            font-size: 22px;
            font-style: normal;
            font-weight: 500;
            line-height: 26px;
          }
        }
        .number {
          @apply border-2 border-solid border-[#00D9EA] rounded-full py-2 px-4 w-[85px] flex items-center justify-center;
        }
      }
    }
  }

  .topics {
    .card {
      @apply text-[#353841] bg-white rounded-md p-4;
      .main-title {
        @apply pl-10 py-10;
        font-size: 18px;
        line-height: 22px;
      }

      .top-area {
        @apply flex justify-between items-center;
        .mini-icon {
          @apply w-[55px] h-[62px] -top-2 relative;
        }
      }

      .content {
        @apply px-10;
        .hex-title {
          @apply py-6;
          font-family: 'tt-commons-med';
          font-size: 22px;
          font-style: normal;
          line-height: 26px;
        }
        .hex-description:deep() {
          @apply pb-4;
          p {
            font-size: 18px;
            line-height: 22px;
            color: #353841;
          }
        }
        .position {
          @apply flex justify-between items-center py-4;
          .numbers {
            @apply border-2 border-solid border-[#00D9EA] rounded-full py-2 px-4 w-fit text-[18px];
          }
          .arrows {
            @apply flex gap-x-8;
            img {
              @apply w-20 h-20 rounded-full;
              box-shadow: 0px 4px 9px 0px rgba(35, 46, 79, 0.5);
              @apply w-20 h-20 transition-all ease-in-out cursor-pointer;
              transform: scale(1);
              &:hover {
                transform: scale(1.1);
              }
              &:first-child {
                transform: rotate(180deg);
                box-shadow: 0px -4px 9px 0px rgba(35, 46, 79, 0.5);
                &:hover {
                  transform: rotate(180deg) scale(1.1);
                }
              }
            }
          }
        }
      }

      .image {
        @apply min-h-[240px] w-full bg-cover bg-center mb-4;
      }
      .video {
        video {
          @apply min-h-[240px] w-full object-cover bg-center mb-4;
        }
      }
    }
  }
}
</style>
